import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  getSinglePages,
  createSinglePage,
  updateSinglePage,
  deleteSinglePage,
  SinglePage
} from "@/utils/adminApi";

// 生成唯一的纯数字slug
function generateNumericSlug(existingSlugs: string[]): string {
  const base = Date.now().toString().slice(-6);
  let slug = base;
  let counter = 1;
  while (existingSlugs.includes(slug)) {
    slug = `${base}-${counter}`;
    counter++;
  }
  return slug;
}

function formatDate(dateStr: string) {
  return dateStr ? dateStr.replace(/-/g, "/") : "-";
}

interface PageEditorProps {
  page: SinglePage | null;
  allPages: SinglePage[];
  onClose: () => void;
  onSave: (page: SinglePage) => void;
}

function PageEditor({ page, allPages, onClose, onSave }: PageEditorProps) {
  const isNew = !page || page.id.startsWith("new-");
  const [form, setForm] = useState<SinglePage>(
    page ?? {
      id: "new-" + Date.now(),
      title: "",
      slug: "",
      content: "",
      sort_order: 0,
      status: "active",
    }
  );
  const [slugManual, setSlugManual] = useState(!isNew);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof SinglePage>(k: K, v: SinglePage[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleTitleChange = (val: string) => {
    set("title", val);
    if (!slugManual) {
      const existingSlugs = allPages.map(p => p.slug).filter(Boolean);
      set("slug", generateNumericSlug(existingSlugs));
    }
  };

  const handleSlugChange = (val: string) => {
    setSlugManual(true);
    set("slug", val.replace(/[^0-9-]/g, '').replace(/--+/g, '-').replace(/^-|-$/g, ''));
  };

  const canSave = form.title.trim() && form.slug.trim();

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const renderMarkdown = (text: string) => {
    return text
      .replace(/^### (.+)$/gm, "<h3 class=\"text-base font-semibold text-gray-800 mt-5 mb-2\">$1</h3>")
      .replace(/^## (.+)$/gm, "<h2 class=\"text-lg font-bold text-gray-900 mt-6 mb-3\">$1</h2>")
      .replace(/^# (.+)$/gm, "<h1 class=\"text-xl font-bold text-gray-900 mt-6 mb-3\">$1</h1>")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/^- (.+)$/gm, "<li class=\"ml-4 list-disc text-gray-600\">$1</li>")
      .replace(/\n\n/g, "</p><p class=\"text-gray-600 leading-relaxed mb-3\">")
      .replace(/^(?!<[h|l|p])(.+)$/gm, "<p class=\"text-gray-600 leading-relaxed mb-3\">$1</p>");
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-4xl shadow-xl flex flex-col"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-800">{isNew ? "新建单页" : "编辑单页"}</h3>
            <div className="flex items-center gap-1 p-0.5 bg-gray-100 rounded-lg">
              <button
                onClick={() => setPreview(false)}
                className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${!preview ? "bg-white text-gray-800 font-medium" : "text-gray-500"}`}
              >
                编辑
              </button>
              <button
                onClick={() => setPreview(true)}
                className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${preview ? "bg-white text-gray-800 font-medium" : "text-gray-500"}`}
              >
                预览
              </button>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {!preview ? (
            <div className="p-6 space-y-5">
              {/* Title + Slug */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1.5">
                    页面标题 <span className="text-red-400">*</span>
                  </label>
                  <input
                    value={form.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="如：关于我们"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1.5">
                    页面路径（slug） <span className="text-red-400">*</span>
                  </label>
                  <div className="flex items-center border border-gray-200 rounded-lg focus-within:border-orange-400 overflow-hidden">
                    <span className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-r border-gray-200 whitespace-nowrap shrink-0">/page/</span>
                    <input
                      value={form.slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="about"
                      className="flex-1 px-3 py-2 text-sm focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    访问地址：<span className="font-mono text-orange-500">/page/{form.slug || "..."}</span>
                  </p>
                </div>
              </div>

              {/* Status Settings */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">启用状态</span>
                <button
                  onClick={() => set("status", form.status === 'active' ? 'hidden' : 'active')}
                  className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${form.status === 'active' ? "bg-orange-500" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.status === 'active' ? "translate-x-4" : "translate-x-0"}`} />
                </button>
                <span className="text-xs text-gray-500">
                  {form.status === 'active' ? '已启用（前台可见）' : '已禁用（显示404）'}
                </span>
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-gray-600">
                    页面内容
                  </label>
                  <span className="text-xs text-gray-400">支持 Markdown 格式</span>
                </div>
                <textarea
                  value={form.content || ''}
                  onChange={(e) => set("content", e.target.value)}
                  placeholder={"## 页面标题\n\n在这里输入页面内容，支持 Markdown 格式...\n\n### 小标题\n\n正文内容\n\n- 列表项1\n- 列表项2"}
                  rows={18}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none font-mono leading-relaxed"
                />
                <p className="text-xs text-gray-400 mt-1">
                  支持：## 标题、**加粗**、- 列表项 等 Markdown 语法
                </p>
              </div>
            </div>
          ) : (
            <div className="p-8 max-w-3xl mx-auto">
              <h1 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b border-gray-100">
                {form.title || "（无标题）"}
              </h1>
              {form.content ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(form.content) }}
                />
              ) : (
                <p className="text-gray-400 text-sm">暂无内容</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <div className="text-xs text-gray-400">
            {form.slug && (
              <span>
                前台链接：
                <a
                  href={`/page/${form.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-500 hover:underline font-mono"
                >
                  /page/{form.slug}
                </a>
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave || saving}
              className="px-5 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <i className="ri-loader-2-line animate-spin" />
                  保存中...
                </>
              ) : "保存发布"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSinglePagesPage() {
  const [pages, setPages] = useState<SinglePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<SinglePage | null | "new">(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  // 加载单页数据
  const loadPages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSinglePages();
      setPages(data || []);
    } catch (err: any) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleToggle = async (id: string) => {
    try {
      const page = pages.find(p => p.id === id);
      if (page) {
        const newStatus = page.status === 'active' ? 'hidden' : 'active';
        await updateSinglePage(id, { status: newStatus });
        await loadPages();
        showToast(`已${newStatus === 'active' ? '启用' : '禁用'}`);
      }
    } catch (err: any) {
      showToast(err.message || '更新失败');
    }
  };

  const handleSave = async (page: SinglePage) => {
    try {
      if (page.id.startsWith("new-")) {
        await createSinglePage({
          title: page.title,
          slug: page.slug,
          content: page.content || '',
          sort_order: page.sort_order || 0,
          status: page.status || 'active',
        });
        showToast('页面已创建');
      } else {
        await updateSinglePage(page.id, {
          title: page.title,
          slug: page.slug,
          content: page.content,
          sort_order: page.sort_order,
          status: page.status,
        });
        showToast('页面已保存');
      }
      setEditTarget(null);
      await loadPages();
    } catch (err: any) {
      showToast(err.message || '保存失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSinglePage(id);
      setDeleteConfirm(null);
      await loadPages();
      showToast('页面已删除');
    } catch (err: any) {
      showToast(err.message || '删除失败');
    }
  };

  const enabledCount = pages.filter((p) => p.status === 'active').length;

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">单页管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理关于我们、帮助中心、协议等独立页面内容</p>
          </div>
          <button
            onClick={() => setEditTarget("new")}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            新建单页
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg">
              <i className="ri-pages-line text-orange-500" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{pages.length}</div>
              <div className="text-xs text-gray-400">总页面数</div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg">
              <i className="ri-checkbox-circle-line text-green-500" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{enabledCount}</div>
              <div className="text-xs text-gray-400">已启用</div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-gray-50 rounded-lg">
              <i className="ri-eye-off-line text-gray-400" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{pages.length - enabledCount}</div>
              <div className="text-xs text-gray-400">已禁用</div>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-lightbulb-line text-orange-500" />
          </div>
          <div className="text-sm text-orange-700">
            <strong>使用说明：</strong>单页内容支持 Markdown 格式，创建后可前往「导航菜单」中添加链接，或在「底部菜单」中配置对应的 <span className="font-mono">/page/slug</span> 路径。
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-3">
            <i className="ri-error-warning-line text-red-500" />
            <span className="text-sm text-red-600">{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">页面标题</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">访问路径</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">更新时间</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">页面状态</th>
                <th className="text-right px-5 py-3 text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-gray-400">
                    <i className="ri-loader-2-line animate-spin mr-2" />
                    加载中...
                  </td>
                </tr>
              ) : pages.map((page) => (
                <tr key={page.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg shrink-0">
                        <i className="ri-file-text-line text-orange-500 text-sm" />
                      </div>
                      <span className="text-sm font-medium text-gray-800">{page.title}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        /page/{page.slug}
                      </span>
                      <button
                        onClick={() => window.open(`/page/${page.slug}`, '_blank')}
                        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors cursor-pointer"
                        title="在新窗口查看"
                      >
                        <i className="ri-external-link-line text-sm" />
                      </button>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs text-gray-400">{formatDate(page.updated_at)}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => handleToggle(page.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                        page.status === 'active'
                          ? "bg-green-50 text-green-600 hover:bg-green-100"
                          : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                      }`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${page.status === 'active' ? "bg-green-500" : "bg-gray-400"}`} />
                      {page.status === 'active' ? "已启用" : "已禁用"}
                    </button>
                  </td>

                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => window.open(`/page/${page.slug}`, '_blank')}
                        className="px-3 py-1.5 text-xs border border-gray-200 text-blue-600 rounded-lg hover:border-blue-400 hover:text-blue-500 cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-eye-line text-xs" />
                        查看
                      </button>
                      <button
                        onClick={() => setEditTarget(page)}
                        className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer whitespace-nowrap"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(page.id)}
                        className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer whitespace-nowrap"
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && pages.length === 0 && (
            <div className="py-16 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                <i className="ri-pages-line text-gray-400 text-xl" />
              </div>
              <p className="text-gray-400 text-sm">暂无单页，点击右上角新建</p>
            </div>
          )}
        </div>
      </div>

      {/* Editor Modal */}
      {editTarget !== null && (
        <PageEditor
          page={editTarget === "new" ? null : editTarget}
          allPages={pages}
          onClose={() => setEditTarget(null)}
          onSave={handleSave}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="text-center font-semibold text-gray-800 mb-2">确认删除</h3>
            <p className="text-center text-sm text-gray-500 mb-6">删除后该页面将无法访问，无法恢复</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 cursor-pointer"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-800 text-white text-sm px-5 py-2.5 rounded-full flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-green-400" />
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}
